import { Collapsible, toast } from "@/components/ui";
import { ChartingConditionSearch } from "@/features/Charting/components/ChartingConditionSearch";
import { Diagnosis } from "@/features/Charting/components/Diagnoses/Diagnosis";
import { useAddDiagnosisToVisit } from "@/features/Charting/network/useAddDiagnosisToVisit";
import type { ChartingCondition } from "@/features/Charting/network/useChartingConditionSearch";
import { useReorderDiagnosisInVisit } from "@/features/Charting/network/useReorderDiagnosisInVisit";
import { useVisitDiagnoses } from "@/features/Charting/network/useVisitDiagnoses";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

interface DisplayDiagnosis {
  identifiers: {
    conditionId?: string;
    snomedCode?: string;
  };
  key: string;
  label: string;
}

export const Diagnoses = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });

  const [pendingDiagnoses, setPendingDiagnoses] = useState<
    Map<string, ChartingCondition>
  >(new Map());
  const [orderedDiagnosisKeys, setOrderedDiagnosisKeys] = useState<
    string[] | null
  >(null);

  const {
    data: visitDiagnoses,
    isLoading: isLoadingVisitDiagnoses,
    isError: isErrorLoadingVisitDiagnoses,
  } = useVisitDiagnoses(encounterId as string);

  const diagnoses: DisplayDiagnosis[] =
    visitDiagnoses?.visitDiagnoses.reduce<DisplayDiagnosis[]>(
      (currentDiagnoses, condition) => {
        const label = condition.displayName.trim();
        if (!label) {
          return currentDiagnoses;
        }

        const conditionId = condition.id?.trim() || undefined;
        const snomedCode = condition.snomedConcept?.code || undefined;
        const baseKey = conditionId ?? snomedCode ?? `diagnosis-${label}`;
        const usedKeys = new Set(currentDiagnoses.map(({ key }) => key));
        let key = baseKey;
        let occurrence = 0;
        while (usedKeys.has(key)) {
          occurrence += 1;
          key = `${baseKey}-${occurrence}`;
        }

        currentDiagnoses.push({
          identifiers: { conditionId, snomedCode },
          key,
          label,
        });

        return currentDiagnoses;
      },
      [],
    ) ?? [];

  const diagnosisKeys = diagnoses.map(({ key }) => key);
  const reconciledDiagnosisKeys = orderedDiagnosisKeys
    ? [
        ...orderedDiagnosisKeys.filter((key) => diagnosisKeys.includes(key)),
        ...diagnosisKeys.filter((key) => !orderedDiagnosisKeys.includes(key)),
      ]
    : diagnosisKeys;
  const orderedDiagnoses = reconciledDiagnosisKeys.flatMap((key) => {
    const diagnosis = diagnoses.find((current) => current.key === key);
    return diagnosis ? [diagnosis] : [];
  });

  useEffect(() => {
    setOrderedDiagnosisKeys((currentKeys) => {
      if (!currentKeys) {
        return diagnosisKeys;
      }

      const nextKeys = [
        ...currentKeys.filter((key) => diagnosisKeys.includes(key)),
        ...diagnosisKeys.filter((key) => !currentKeys.includes(key)),
      ];
      if (
        nextKeys.length === currentKeys.length &&
        nextKeys.every((key, index) => key === currentKeys[index])
      ) {
        return currentKeys;
      }

      return nextKeys;
    });
  }, [diagnosisKeys]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const reorderDiagnosisInVisit = useReorderDiagnosisInVisit({
    patientId,
    motherEncounterId: encounterId as string,
    onSuccess: () => setOrderedDiagnosisKeys(null),
    onError: () => {
      setOrderedDiagnosisKeys(null);
      toast.error("Failed to reorder diagnoses");
    },
  });

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const fromIndex = reconciledDiagnosisKeys.indexOf(String(active.id));
    const toIndex = reconciledDiagnosisKeys.indexOf(String(over.id));
    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    const diagnosis = orderedDiagnoses[fromIndex];
    if (!diagnosis?.identifiers.conditionId) {
      return;
    }

    const nextKeys = [...reconciledDiagnosisKeys];
    const [movedKey] = nextKeys.splice(fromIndex, 1);
    nextKeys.splice(toIndex, 0, movedKey);
    setOrderedDiagnosisKeys(nextKeys);
    reorderDiagnosisInVisit.mutate({
      conditionId: diagnosis.identifiers.conditionId,
      position: toIndex + 1,
    });
  };

  const visitDiagnosisCodes = new Set(
    diagnoses.flatMap(({ identifiers }) => {
      const snomedCode = identifiers.snomedCode;
      return snomedCode ? [snomedCode] : [];
    }),
  );

  const visiblePendingDiagnoses = [...pendingDiagnoses].filter(
    ([, diagnosis]) => !visitDiagnosisCodes.has(diagnosis.conditionCode),
  );
  const totalDiagnoses = diagnoses.length + visiblePendingDiagnoses.length;
  const pendingConditionCodes = new Set(pendingDiagnoses.keys());

  // Reconcile pending diagnoses with visit diagnoses to remove any that have been added to the visit.
  // This is to avoid flickering between the add Dx settling and the get Dx refetching.
  useEffect(() => {
    setPendingDiagnoses((current) => {
      const currentVisitDiagnosisCodes = new Set(
        visitDiagnoses?.visitDiagnoses.flatMap((diagnosis) => {
          const code = diagnosis.snomedConcept?.code;
          return code ? [code] : [];
        }),
      );
      const reconciledCodes = [...current.keys()].filter((code) =>
        currentVisitDiagnosisCodes.has(code),
      );

      if (reconciledCodes.length === 0) {
        return current;
      }

      const next = new Map(current);
      for (const code of reconciledCodes) {
        next.delete(code);
      }

      return next;
    });
  }, [visitDiagnoses?.visitDiagnoses]);

  const addDiagnosisToVisit = useAddDiagnosisToVisit({
    patientId,
    motherEncounterId: encounterId as string,
    onError: () => toast.error("Failed to add diagnosis to visit"),
  });

  const addDiagnosis = (condition: ChartingCondition) => {
    const key = condition.conditionCode;

    if (pendingDiagnoses.has(key)) {
      return;
    }

    setPendingDiagnoses((current) => new Map(current).set(key, condition));
    void addDiagnosisToVisit
      .mutateAsync({
        diagnosisSnomedCode: condition.conditionCode,
        diagnosis: condition.conditionDisplay,
      })
      .catch(() => {
        setPendingDiagnoses((current) => {
          const next = new Map(current);
          next.delete(key);
          return next;
        });
      });
  };

  if (isLoadingVisitDiagnoses || isErrorLoadingVisitDiagnoses) {
    return null;
  }

  return (
    <Collapsible
      allowOverflow
      data-testid="diagnoses"
      defaultOpen
      status={totalDiagnoses ? totalDiagnoses.toString() : "None"}
      title="Diagnoses"
    >
      <ChartingConditionSearch
        aria-label="Search diagnoses"
        patientId={patientId}
        motherEncounterId={encounterId as string}
        onConditionSelected={(condition) => {
          if (condition) {
            addDiagnosis(condition);
          }
        }}
        pendingConditionCodes={pendingConditionCodes}
        clearOnSelect={true}
        placeholder="Search for a diagnosis"
      />

      <div className="mt-2 flex flex-col gap-1">
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={reconciledDiagnosisKeys}
            strategy={verticalListSortingStrategy}
          >
            {orderedDiagnoses.map((diagnosis) => (
              <Diagnosis
                conditionId={diagnosis.identifiers.conditionId}
                isReordering={reorderDiagnosisInVisit.isPending}
                key={diagnosis.key}
                label={diagnosis.label}
                motherEncounterId={encounterId as string}
                patientId={patientId}
                sortableId={diagnosis.key}
              />
            ))}
          </SortableContext>
          {visiblePendingDiagnoses.map(([key, diagnosis]) => (
            <Diagnosis
              key={`pending-${key}`}
              label={diagnosis.conditionDisplay}
              motherEncounterId={encounterId as string}
              pending
              patientId={patientId}
              sortableId={`pending-${key}`}
            />
          ))}
        </DndContext>
      </div>
    </Collapsible>
  );
};
